import React, { Component } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

/**
 * Error Boundary Component
 * Catches and handles React component errors gracefully
 * Renders a safe, user-friendly fallback UI without exposing stack traces in production
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console (for debugging in development)
    // In production, this will be handled by error reporting services
    if (process.env.NODE_ENV === "development") {
      console.error("Error caught by Error Boundary:", error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default safe error UI - no stack traces or sensitive data
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            bgcolor: "background.default",
            p: 3,
          }}
        >
          <Card
            sx={{
              maxWidth: 600,
              width: "100%",
              textAlign: "center",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: "text.primary" }}>
                Something went wrong
              </Typography>

              <Typography
                variant="body1"
                sx={{ mb: 3, color: "text.secondary" }}
              >
                We're sorry for the inconvenience. An unexpected error has
                occurred. Please try again or return to the home page.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <Button variant="contained" onClick={this.handleReset}>
                  Try Again
                </Button>
                <Button variant="outlined" onClick={this.handleGoHome}>
                  Go Home
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Wrap your components with the ErrorBoundary
